import { useDisclosure } from '@chakra-ui/react';
import {
  ListingForSearchMlsListingFragment,
  MlsListingFiltersInput,
  MlsListingMlsFragment,
  MlsListingSearchResultFragment,
  PropertyDetailsFragment,
} from '@client/graphql/__generated__/types';
import { GeocodingFeature } from '@mapbox/search-js-core';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { MapRef } from 'react-map-gl';
import { useExploreContextQueries } from './hooks/useExploreContextQueries';

export const ADDRESS_FEATURE_TYPES = ['address', 'secondary_address'];

export interface MlsListingWithListing {
  mlsListing: MlsListingSearchResultFragment;
  listing?: ListingForSearchMlsListingFragment;
}

interface ExploreContextType {
  listings: MlsListingWithListing[];
  loading: boolean;
  filters: MlsListingFiltersInput;
  updateFilters: (filters: Partial<MlsListingFiltersInput>) => void;
  clearFilters: () => void;
  selectedListing: MlsListingWithListing[];
  setSelectedListing: (listing: MlsListingWithListing[]) => void;
  totalCount: number;
  setTotalCount: (totalCount: number) => void;
  searchResult: GeocodingFeature | null;
  setSearchResult: (searchResult: GeocodingFeature | null) => void;
  shouldOpenPopup: boolean;
  setShouldOpenPopup: (shouldOpenPopup: boolean) => void;
  mapRef: React.RefObject<MapRef>;
  handleRetrieve: (result: GeocodingFeature) => Promise<void>;
  utmParams?: { [key: string]: string };
  hasFilters: boolean;
  showClosedStatus: boolean;
  isAddOfferScorecardModalOpen: boolean;
  onOpenAddOfferScorecardModal: () => void;
  onCloseAddOfferScorecardModal: () => void;
  shouldPromptForOffMarketListing: boolean;
  setShouldPromptForOffMarketListing: (
    shouldPromptForOffMarketListing: boolean
  ) => void;
  foundPropertyDetails: PropertyDetailsFragment | null;
  setFoundPropertyDetails: (
    foundPropertyDetails: PropertyDetailsFragment | null
  ) => void;
  disclaimers: MlsListingMlsFragment[];
}

export const ExploreContext = createContext<ExploreContextType>(
  {} as ExploreContextType
);

interface ExploreProviderProps extends PropsWithChildren {
  utmParams?: { [key: string]: string };
  showClosedStatus?: boolean;
  skipMlsListingsSearch?: boolean;
}

export const ExploreProvider = ({
  children,
  utmParams,
  showClosedStatus = false,
  skipMlsListingsSearch = false,
}: ExploreProviderProps) => {
  const mapRef = useRef<MapRef>(null);
  const {
    isOpen: isAddOfferScorecardModalOpen,
    onOpen: onOpenAddOfferScorecardModal,
    onClose: onCloseAddOfferScorecardModal,
  } = useDisclosure();

  const {
    listings,
    totalCount,
    filters,
    updateFilters,
    clearFilters,
    selectedListing,
    setSelectedListing,
    foundMLSListing,
    loading,
    shouldOpenPopup,
    setShouldOpenPopup,
    shouldPromptForOffMarketListing,
    setShouldPromptForOffMarketListing,
    hasFilters,
    setTotalCount,
    handleRetrieve,
    searchResult,
    setSearchResult,
    foundPropertyDetails,
    setFoundPropertyDetails,
    disclaimers,
  } = useExploreContextQueries({
    showClosedStatus,
    skipMlsListingsSearch,
    mapRef,
  });

  useEffect(() => {
    mapRef?.current?.getMap()?.resize();
  }, [mapRef]);

  return (
    <ExploreContext.Provider
      value={{
        handleRetrieve,
        selectedListing,
        setSelectedListing,
        listings: foundMLSListing ? [...listings, foundMLSListing] : listings,
        loading: loading,
        filters,
        updateFilters,
        clearFilters,
        totalCount,
        setTotalCount,
        searchResult,
        setSearchResult,
        shouldOpenPopup,
        setShouldOpenPopup,
        mapRef,
        utmParams,
        hasFilters,
        showClosedStatus,
        isAddOfferScorecardModalOpen,
        onOpenAddOfferScorecardModal,
        onCloseAddOfferScorecardModal,
        shouldPromptForOffMarketListing,
        setShouldPromptForOffMarketListing,
        foundPropertyDetails,
        setFoundPropertyDetails,
        disclaimers,
      }}
    >
      {children}
    </ExploreContext.Provider>
  );
};

export const useExploreContext = () => {
  return useContext(ExploreContext);
};
